import React from 'react';
import Icon from '../../../components/AppIcon';

const BenefitsSection = () => {
  const benefits = [
    {
      icon: 'Users',
      iconColor: 'var(--color-primary)',
      title: 'Reduce Employee Churn',
      metric: '-67%',
      description: 'Structured onboarding creates confident, engaged employees who stay longer and contribute more value to your organization',
      stats: [
        { label: 'First-year retention', value: '94%' },
        { label: 'Employee satisfaction', value: '4.8/5' }
      ]
    },
    {
      icon: 'TrendingUp',
      iconColor: 'var(--color-success)',
      title: 'Accelerate Productivity',
      metric: '3x Faster',
      description: 'Systematic knowledge delivery and AI assistance help new hires become productive contributors in days instead of weeks',
      stats: [
        { label: 'Time to first commit', value: '2.3 days' },
        { label: 'Onboarding completion', value: '89%' }
      ]
    },
    {
      icon: 'Euro',
      iconColor: 'var(--color-accent)',
      title: 'Massive Cost Savings',
      metric: '€77.000',
      description: 'Reduce manager time spent on repetitive questions and eliminate costs associated with early employee turnover',
      stats: [
        { label: 'Manager time saved', value: '80%' },
        { label: 'ROI within', value: '3 months' }
      ]
    }
  ];

  return (
    <section className="py-20 px-6 bg-background">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
            Measurable Business Impact
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Real results from companies that transformed their onboarding with Bee Wise
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {benefits?.map((benefit, index) => (
            <div
              key={index}
              className="bg-card border border-border rounded-lg p-8 hover:shadow-elevation-2 hover:scale-102 transition-all duration-300"
            >
              <div className="w-16 h-16 bg-muted/30 rounded-lg flex items-center justify-center mb-6">
                <Icon name={benefit?.icon} size={32} color={benefit?.iconColor} strokeWidth={2} />
              </div>
              
              <div className="mb-4">
                <h3 className="text-3xl font-bold text-primary mb-2">{benefit?.metric}</h3>
                <h4 className="text-xl font-semibold text-foreground mb-3">{benefit?.title}</h4>
                <p className="text-muted-foreground leading-relaxed">{benefit?.description}</p>
              </div>

              <div className="border-t border-border pt-4 mt-6 space-y-3">
                {benefit?.stats?.map((stat, statIndex) => (
                  <div key={statIndex} className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">{stat?.label}</span>
                    <span className="text-sm font-semibold text-foreground">{stat?.value}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="bg-card border border-border rounded-lg p-8">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-2xl font-bold text-foreground mb-4">
                Enterprise-Grade Security & Compliance
              </h3>
              <p className="text-muted-foreground mb-6">
                Built for B2B SaaS companies with the highest security standards and compliance requirements
              </p>
              <div className="space-y-3">
                {[
                  { icon: 'Shield', text: 'SOC 2 Type II Certified' },
                  { icon: 'Lock', text: 'GDPR & CCPA Compliant' },
                  { icon: 'Key', text: 'SSO & SAML Integration' },
                  { icon: 'Database', text: 'End-to-End Encryption' }
                ]?.map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-success/10 rounded-lg flex items-center justify-center">
                      <Icon name={item?.icon} size={16} color="var(--color-success)" />
                    </div>
                    <span className="text-foreground">{item?.text}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-muted/20 rounded-lg p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center">
                  <Icon name="Award" size={24} color="var(--color-primary)" strokeWidth={2.5} />
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-foreground">Trusted by Industry Leaders</h4>
                  <p className="text-sm text-muted-foreground">500+ companies worldwide</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { label: 'Uptime SLA', value: '99.9%' },
                  { label: 'Data Centers', value: '12+' },
                  { label: 'Support Response', value: '&lt;1hr' },
                  { label: 'Customer Rating', value: '4.9/5' }
                ]?.map((metric, index) => (
                  <div key={index} className="text-center">
                    <div className="text-2xl font-bold text-primary mb-1" dangerouslySetInnerHTML={{ __html: metric?.value }} />
                    <div className="text-xs text-muted-foreground">{metric?.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BenefitsSection;